import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { fromEvent, throttleTime } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

const DEFAULT_DURATION = 300;

@Component({
  selector: 'ef-text-collapse',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule],
  animations: [
    trigger('collapse', [
      state('false', style({ height: '*' })),
      state('true', style({ height: '{{initialHeight}}px' }), { params: { initialHeight: 0 } }),
      transition('false => true', animate(DEFAULT_DURATION + 'ms ease-in')),
      transition('true => false', animate(DEFAULT_DURATION + 'ms ease-out')),
    ]),
  ],
  template: `
    <div
      class="overflow-hidden"
      [@collapse]="{
        value: collapsed(),
        params: { initialHeight: initialHeight() },
      }"
      (@collapse.done)="collapseAnimationDone()"
    >
      <p class="overflow-hidden">
        <span class="whitespace-break-spaces" #initialEl
          ><ng-content select="[initial]"></ng-content>{{ textTruncate() && showDelimiter() ? '...' : '' }}</span
        ><ng-content select="[collapse]" *ngIf="!textTruncate()"></ng-content>
        <a #triggerButton class="underline cursor-pointer ml-1" (click)="toggleCollapse($event)"
          ><ng-container *ngIf="collapsed()">{{ expandText() || defaultExpandText }}</ng-container
          ><ng-container *ngIf="!collapsed()">{{ collapseText() || defaultCollapseText }}</ng-container></a
        >
      </p>
    </div>
  `,
})
@UntilDestroy()
export class EfTextCollapseComponent implements AfterViewInit {
  public readonly collapsed = model<boolean>(true);
  public readonly showDelimiter = input<boolean>(true);
  public readonly expandText = input<string>();
  public readonly collapseText = input<string>();

  public readonly textTruncate = signal<boolean>(this.collapsed());
  public readonly initialHeight = signal<number>(0);

  public initialEl = viewChild.required<ElementRef>('initialEl');
  public triggerButton = viewChild.required<ElementRef>('triggerButton');

  private readonly translate = inject(TranslateService);
  protected readonly defaultExpandText = this.translate.instant('expand');
  protected readonly defaultCollapseText = this.translate.instant('collapse');

  public ngAfterViewInit(): void {
    // Move task to end event loop after animation is done
    setTimeout(() => this.updateAnimationHeight(), 0);

    fromEvent(window, 'resize')
      .pipe(throttleTime(200), untilDestroyed(this))
      .subscribe(() => this.updateAnimationHeight());
  }

  public toggleCollapse(event: MouseEvent): void {
    event.stopPropagation();
    event.stopImmediatePropagation();
    event.preventDefault();

    if (this.textTruncate()) {
      this.textTruncate.set(!this.textTruncate());
    }

    this.collapsed.set(!this.collapsed());
  }

  public collapseAnimationDone(): void {
    if (this.collapsed()) {
      this.textTruncate.set(this.collapsed());

      //Ponowne wyliczenie wysokości początkowego tekstu po zakończeniu animacji
      //Gdyby zmienił się rozmiar kontenera, który miałby wpływ na zawinięcie tekstu
      setTimeout(() => {
        this.updateAnimationHeight();
      }, DEFAULT_DURATION);
    }
  }

  private updateAnimationHeight(): void {
    if (!this.collapsed()) {
      return;
    }

    const { bottom: triggerButtonBottom } = this.triggerButton().nativeElement.getBoundingClientRect();
    const { top: initialContentTop } = this.initialEl().nativeElement.getBoundingClientRect();

    this.initialHeight.set(triggerButtonBottom - initialContentTop + 5);
  }
}
